// ActionQueue.jsx
// Shows pending actions that need the club owner's attention

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaBuilding, FaClipboardList, FaUsers, FaExclamationTriangle,
  FaArrowRight
} from 'react-icons/fa';
import styles from './ActionQueue.module.css';

const ActionQueue = ({
  clubId,
  pendingClubApprovals,
  pendingProgramApps,
  pendingTeamInvites,
  issues,
  totalActions,
  loading
}) => {
  const navigate = useNavigate();

  const handleClubApprovals = () => {
    navigate(`/club/${clubId}`, { 
      state: { openModal: 'management', activeTab: 'requests' } 
    });
  };

  const handleProgramApps = () => {
    navigate(`/clubs/${clubId}`);
  };

  const handleTeamInvites = () => {
    navigate(`/clubs/${clubId}`);
  };

  const handleIssues = () => {
    navigate(`/clubs/${clubId}`);
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.headerIcon}>🔔</span>
            <h2 className={styles.headerTitle}>Action Queue</h2>
          </div>
        </div>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
          <span>Loading actions...</span>
        </div>
      </div>
    );
  }

  // No longer show empty state - always show tiles for navigation

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.headerIcon}>🔔</span>
          <h2 className={styles.headerTitle}>Action Queue</h2>
          <span className={styles.subtitle}>Things that need your attention</span>
        </div>
        <div className={styles.headerRight}>
          <span className={styles.totalBadge}>{totalActions} total</span>
        </div>
      </div>

      <div className={styles.actionsGrid}>
        {/* Club Member Approvals */}
        <button 
          className={`${styles.actionCard} ${pendingClubApprovals > 0 ? styles.hasItems : styles.noItems}`}
          onClick={handleClubApprovals}
        >
          <div className={styles.cardIcon}>
            <FaBuilding />
          </div>
          <div className={styles.cardContent}>
            <span className={styles.cardCount}>{pendingClubApprovals}</span>
            <span className={styles.cardLabel}>Club Membership</span>
            <span className={styles.cardDesc}>
              People requesting to join club
            </span>
          </div>
          <div className={styles.cardAction}>
            <span>{pendingClubApprovals > 0 ? 'Review' : 'Manage'}</span>
            <FaArrowRight />
          </div>
        </button>

        {/* Program Applications */}
        <button 
          className={`${styles.actionCard} ${pendingProgramApps > 0 ? styles.hasItems : styles.noItems}`}
          onClick={handleProgramApps}
        >
          <div className={styles.cardIcon}>
            <FaClipboardList />
          </div>
          <div className={styles.cardContent}>
            <span className={styles.cardCount}>{pendingProgramApps}</span>
            <span className={styles.cardLabel}>Program Applications</span>
            <span className={styles.cardDesc}>
              Registration apps pending review
            </span>
          </div>
          <div className={styles.cardAction}>
            <span>{pendingProgramApps > 0 ? 'Review' : 'View'}</span>
            <FaArrowRight />
          </div>
        </button>

        {/* Team Invites */}
        <button 
          className={`${styles.actionCard} ${pendingTeamInvites > 0 ? styles.hasItems : styles.noItems}`}
          onClick={handleTeamInvites}
        >
          <div className={styles.cardIcon}>
            <FaUsers />
          </div>
          <div className={styles.cardContent}>
            <span className={styles.cardCount}>{pendingTeamInvites}</span>
            <span className={styles.cardLabel}>Team Invites</span>
            <span className={styles.cardDesc}>
              Sent invitations awaiting response
            </span>
          </div>
          <div className={styles.cardAction}>
            <span>View</span>
            <FaArrowRight />
          </div>
        </button>

        {/* Issues */}
        <button 
          className={`${styles.actionCard} ${styles.issueCard} ${issues > 0 ? styles.hasItems : styles.noItems}`}
          onClick={handleIssues}
        >
          <div className={styles.cardIcon}>
            <FaExclamationTriangle />
          </div>
          <div className={styles.cardContent}>
            <span className={styles.cardCount}>{issues}</span>
            <span className={styles.cardLabel}>Needs Attention</span>
            <span className={styles.cardDesc}>
              Programs without directors assigned
            </span>
          </div>
          <div className={styles.cardAction}>
            <span>{issues > 0 ? 'Fix' : 'View'}</span>
            <FaArrowRight />
          </div>
        </button>
      </div>
    </div>
  );
};

export default ActionQueue;

